import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Toggle from "common/Toggle";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { createBlogCategories } from "services/apiService";

interface BlogCategoryFormData {
  name: string;
  is_active: number;
}

interface Errors {
  name?: string;
}

const BlogCategoryCreate: React.FC<{
  open: boolean;
  handleClose: () => void;
  onActionComplete: () => void;
}> = ({ open, handleClose, onActionComplete }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [BlogCategoryformData, setBlogCategoryformData] =
    useState<BlogCategoryFormData>({
      name: "",
      is_active: 0,
    });

  const [errors, setErrors] = useState<Errors>({});

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setBlogCategoryformData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    if (errors[id as keyof Errors]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [id]: "",
      }));
    }
  };

  const handleToggleChange = (checked: number, field: string) => {
    setBlogCategoryformData((prevData) => ({
      ...prevData,
      [field]: checked ? 1 : 0,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    if (!BlogCategoryformData.name.trim()) {
      newErrors.name = "Blog category title is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: BlogCategoryformData.name,
      is_active: BlogCategoryformData.is_active,
    };

    try {
      const response = await createBlogCategories(payload);
      if (response.status_code === 200) {
        toast.success("Blog category created successfully!");
        setBlogCategoryformData({ name: "", is_active: 0 });
        setErrors({});
        handleClose();
        onActionComplete();
      }
    } catch (error) {
      toast.error("Failed to create blog category.");
    }
  };

  const handleCloseDialog = () => {
    setErrors({});
    setBlogCategoryformData({ name: "", is_active: 0 });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 3,
          padding: 2,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          textAlign: "center",
          paddingBottom: 1,
        }}
      >
        Create Blog Category
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ paddingX: 2, paddingY: 1 }}>
            <Box mb={3}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: theme.palette.text.primary, marginBottom: 1 }}
              >
                Blog Category Title
              </Typography>
              <TextField
                variant="outlined"
                id="name"
                value={BlogCategoryformData.name}
                onChange={handleTextFieldChange}
                placeholder="Enter category title"
                fullWidth
                error={!!errors.name}
                helperText={errors.name || ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ width: "25%" }}
              >
                Status
              </Typography>
              <Toggle
                checked={BlogCategoryformData.is_active === 1}
                onChange={(checked) => handleToggleChange(checked, "is_active")}
              />
            </Box>
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", paddingBottom: 2 }}>
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          size="large"
          sx={{
            borderRadius: 2,
            paddingX: 3,
            fontWeight: "bold",
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            paddingX: 3,
            fontWeight: "bold",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          &nbsp;&nbsp; Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogCategoryCreate;
